import { sum } from '../../../../../../utils';
import { formatMetricValue } from '../../../../../../utils-display';
import { TableData } from '../../../../tableview/types';
import { TableViewComponentProps } from '../../../../ui-components/data-viz-ui/table/table-view-component-types';
import {
  ComponentStateManager,
  DataVizContainerCommonUtils,
} from '../../../common/types';
import { getMuiLineChartTableDataFromChartData } from '../../../common/utils';
import { getLineChartDataFromOverTimeSegmentedResponse } from '../../over-time-segmented-utils';
import {
  OverTimeSegmentedVizData,
  OverTimeSegmentedVizDataResponseForMetric,
  OverTimeSegmentedVizExternalProps,
} from '../../types';
import {
  OverTimeSegmentedTableViewActions,
  OverTimeSegmentedTableViewState,
} from './types';

/**
 * Adds a total column to the table data by summing up all segment values for each date.
 * The totals are computed on-the-fly and formatted using the metric's value formatter
 * to ensure consistent formatting across all cells.
 */
const addTotalColumnToTableData = (
  tableData: TableData,
  dataResponse: OverTimeSegmentedVizDataResponseForMetric,
  valueFormatter: (val: number) => string,
  t: DataVizContainerCommonUtils['t']
): TableData => {
  // Build a map of dateLabel -> total by summing all datapoint values
  const totalsByDateLabel = new Map(
    dataResponse.data.map((d) => [
      d.dateLabel,
      sum(d.datapoints.map((dp) => dp.datapoint.value)),
    ])
  );

  const totalHeader = { value: t('common:commonValues.misc.total') };
  const headers = [...tableData.headers, totalHeader];

  const rows = tableData.rows.map((row) => {
    // row.title is the dateLabel
    const total = totalsByDateLabel.get(row.title) ?? 0;
    const formattedTotal = valueFormatter(total);

    return {
      ...row,
      cells: [...row.cells, { key: `${row.key}-total`, value: formattedTotal }],
    };
  });

  return { headers, rows };
};

export const overTimeSegmentedTableViewDataToUiComponentProps = (
  data: OverTimeSegmentedVizData,
  externalProps: OverTimeSegmentedVizExternalProps,
  componentStateManager: ComponentStateManager<
    OverTimeSegmentedTableViewState,
    OverTimeSegmentedTableViewActions
  >,
  { t, dataFieldValueFormatter }: DataVizContainerCommonUtils
): TableViewComponentProps => {
  const { dataResponse, titleConfig, metricDetailsMap, showTotal } = data;
  const { preferences, fullScreenHandle } = externalProps;

  const { metric } = dataResponse;
  const metricDetail = metricDetailsMap[metric.value];
  if (!metricDetail) {
    throw new Error(
      `Invalid state. Metric details undefined for metric: ${metric.value}`
    );
  }

  const chartData = getLineChartDataFromOverTimeSegmentedResponse(
    dataResponse,
    metricDetail,
    preferences,
    t,
    dataFieldValueFormatter
  );

  let tableData = getMuiLineChartTableDataFromChartData(
    chartData,
    {
      selectedSortOrder: null,
    },
    t
  );

  if (showTotal) {
    const valueFormatter = (val: number) =>
      formatMetricValue(val, metricDetail.formatType);
    tableData = addTotalColumnToTableData(
      tableData,
      dataResponse,
      valueFormatter,
      t
    );
  }

  const displayConfig = {
    ...componentStateManager.state.displayState,
    ...componentStateManager.actions.displayActions,
  };
  return {
    fullScreenHandle,
    displayConfig,
    titleConfig,
    tableData,
  };
};
